package com.bikompanion.ui.watch

import com.mmk.kmpnotifier.KMPNotifier
import com.mmk.kmpnotifier.notification.PayloadData
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.BikeType
import com.bikompanion.domain.StationWatch
import com.bikompanion.ui.strings.BikompanionStrings
import com.bikompanion.ui.strings.getLocaleStrings

class StationWatchNotificationListener(
    private val onStationSelected: (StationNotificationTarget) -> Unit,
) : KMPNotifier.Listener {
    init {
        KMPNotifier.addListener(this)
        StationNotificationClicks.register(this)
        prepareStationWatchNotificationClicks()
    }

    override fun onNotificationClicked(data: PayloadData) {
        data.toStationNotificationTarget()?.let(StationNotificationClicks::dispatch)
    }

    fun close() {
        KMPNotifier.removeListener(this)
        StationNotificationClicks.unregister(this)
    }

    internal fun onStationSelected(target: StationNotificationTarget) {
        onStationSelected.invoke(target)
    }
}

object StationWatchNotifications {
    fun postFound(watches: List<StationWatch>) {
        val strings = getLocaleStrings()
        watches.forEach { watch ->
            notify(
                kind = "found",
                title = strings.notificationFoundTitle,
                body = watch.foundBody(strings),
                watch = watch,
            )
        }
    }

    fun postExpired(watches: List<StationWatch>) {
        val strings = getLocaleStrings()
        watches.forEach { watch ->
            notify(
                kind = "expired",
                title = strings.notificationExpiredTitle,
                body = watch.expiredBody(strings),
                watch = watch,
            )
        }
    }

    private fun notify(
        kind: String,
        title: String,
        body: String,
        watch: StationWatch,
    ) {
        postStationWatchNotification(
            id = "$kind:${watch.networkId}:${watch.stationId}:${watch.bikeType.name}".hashCode() and Int.MAX_VALUE,
            title = title,
            body = body,
            target = watch.notificationTarget(),
        )
    }
}

private object StationNotificationClicks {
    private var listener: StationWatchNotificationListener? = null
    private val pending = mutableListOf<StationNotificationTarget>()

    fun register(newListener: StationWatchNotificationListener) {
        listener = newListener
        pending.toList().forEach(newListener::onStationSelected)
        pending.clear()
    }

    fun unregister(oldListener: StationWatchNotificationListener) {
        if (listener === oldListener) listener = null
    }

    fun dispatch(target: StationNotificationTarget) {
        listener?.onStationSelected(target) ?: pending.add(target)
    }
}

internal fun selectStationFromNotification(target: StationNotificationTarget) {
    StationNotificationClicks.dispatch(target)
}

internal expect fun postStationWatchNotification(
    id: Int,
    title: String,
    body: String,
    target: StationNotificationTarget,
)

internal expect fun prepareStationWatchNotificationClicks()

internal const val NetworkIdKey = "station_watch_network_id"
internal const val StationIdKey = "station_watch_station_id"
internal const val StationNameKey = "station_watch_station_name"
internal const val LatitudeKey = "station_watch_latitude"
internal const val LongitudeKey = "station_watch_longitude"
internal const val FreeBikesKey = "station_watch_free_bikes"
internal const val EmptySlotsKey = "station_watch_empty_slots"
internal const val ElectricBikesKey = "station_watch_electric_bikes"
internal const val SupportsElectricBikesKey = "station_watch_supports_electric_bikes"

internal fun StationNotificationTarget.toPayloadData(): Map<String, String> =
    buildMap {
        put(NetworkIdKey, networkId)
        put(StationIdKey, stationId)
        put(StationNameKey, stationName)
        station?.let {
            put(LatitudeKey, it.latitude.toString())
            put(LongitudeKey, it.longitude.toString())
            put(FreeBikesKey, it.freeBikes.toString())
            put(EmptySlotsKey, it.emptySlots.toString())
            put(ElectricBikesKey, it.electricBikes.toString())
            put(SupportsElectricBikesKey, it.supportsElectricBikes.toString())
        }
    }

internal fun PayloadData.toStationNotificationTarget(): StationNotificationTarget? {
    val networkId = this[NetworkIdKey].stringValue()?.takeIf(String::isNotBlank) ?: return null
    val stationId = this[StationIdKey].stringValue()?.takeIf(String::isNotBlank) ?: return null
    val stationName = this[StationNameKey].stringValue().orEmpty()
    val latitude = this[LatitudeKey].doubleValue()?.takeIf { it.isFinite() && it in -90.0..90.0 }
    val longitude = this[LongitudeKey].doubleValue()?.takeIf { it.isFinite() && it in -180.0..180.0 }
    val freeBikes = this[FreeBikesKey].intValue()?.takeIf { it >= 0 }
    val emptySlots = this[EmptySlotsKey].intValue()?.takeIf { it >= 0 }
    val electricBikes = this[ElectricBikesKey].intValue()?.takeIf { it >= 0 && (freeBikes == null || it <= freeBikes) }
    val station = if (
        latitude != null &&
        longitude != null &&
        freeBikes != null &&
        emptySlots != null &&
        electricBikes != null
    ) {
        BikeStation(
            id = stationId,
            name = stationName,
            networkId = networkId,
            latitude = latitude,
            longitude = longitude,
            freeBikes = freeBikes,
            emptySlots = emptySlots,
            electricBikes = electricBikes,
            supportsElectricBikes = this[SupportsElectricBikesKey].booleanValue() ?: false,
        )
    } else {
        null
    }
    return StationNotificationTarget(networkId, stationId, stationName, station)
}

private fun StationWatch.notificationTarget() =
    StationNotificationTarget(
        networkId = networkId,
        stationId = stationId,
        stationName = station?.name ?: stationName,
        station = station,
    )

private fun Any?.stringValue(): String? = this as? String

private fun Any?.doubleValue(): Double? =
    when (this) {
        is Number -> toDouble()
        is String -> toDoubleOrNull()
        else -> null
    }

private fun Any?.intValue(): Int? =
    when (this) {
        is Number -> toInt()
        is String -> toIntOrNull()
        else -> null
    }

private fun Any?.booleanValue(): Boolean? =
    when (this) {
        is Boolean -> this
        is String -> toBooleanStrictOrNull()
        else -> null
    }

private fun StationWatch.foundBody(strings: BikompanionStrings) =
    when (bikeType) {
        BikeType.Normal -> strings.notificationFoundNormal(stationName)
        BikeType.Electric -> strings.notificationFoundElectric(stationName)
    }

private fun StationWatch.expiredBody(strings: BikompanionStrings) =
    when (bikeType) {
        BikeType.Normal -> strings.notificationExpiredNormal(stationName)
        BikeType.Electric -> strings.notificationExpiredElectric(stationName)
    }
